/** Mirror of `socaity_schemas.platform.ai_service`. */

import { AIModel } from './ai_model';
import { Deployment } from './deployment';
import { EndpointPricing, EndpointStats } from './pricing';

const num = (v: any): number | null => (v != null ? Number(v) : null);
const arr = (v: any): any[] => (Array.isArray(v) ? v : []);

export class ServiceCategory {
  id: string | null;
  name: string | null;
  display_name: string | null;
  description: string | null;
  short_desc: string | null;
  input_modalities: string[];
  output_modalities: string[];

  constructor(data: Record<string, any> = {}) {
    this.id = data.id ?? null;
    this.name = data.name ?? null;
    this.display_name = data.display_name ?? null;
    this.description = data.description ?? null;
    this.short_desc = data.short_desc ?? null;
    this.input_modalities = arr(data.input_modalities);
    this.output_modalities = arr(data.output_modalities);
  }
}

/** Platform metadata of one endpoint. Technical shape lives on the deployment contract. */
export class ServiceEndpoint {
  id: string | null;
  service_id: string | null;
  path: string | null;
  display_name: string | null;
  description: string | null;
  description_agent: string | null;
  description_agent_updated_at: string | null;
  description_user_updated_at: string | null;
  is_public: boolean;
  nsfw: boolean;
  model_ids: string[];
  n_usages: number | null;
  mean_execution_time_ms: number | null;
  stats: EndpointStats | null;

  constructor(data: Record<string, any> = {}) {
    this.id = data.id ?? null;
    this.service_id = data.service_id ?? null;
    this.path = data.path ?? null;
    this.display_name = data.display_name ?? null;
    this.description = data.description ?? null;
    this.description_agent = data.description_agent ?? null;
    this.description_agent_updated_at = data.description_agent_updated_at ?? null;
    this.description_user_updated_at = data.description_user_updated_at ?? null;
    this.is_public = data.is_public !== undefined ? data.is_public : true;
    this.nsfw = data.nsfw === true;
    this.model_ids = arr(data.model_ids);
    this.n_usages = num(data.n_usages);
    this.mean_execution_time_ms = num(data.mean_execution_time_ms);
    this.stats = data.stats ? new EndpointStats(data.stats) : null;
  }
}

/** Aggregate root of the AI catalog. */
export class AIService {
  id: string | null;
  name: string | null;
  display_name: string | null;
  description: string | null;
  description_agent: string | null;
  description_agent_updated_at: string | null;
  description_user_updated_at: string | null;
  short_desc: string | null;
  display_img_url: string | null;
  github_url: string | null;
  origin_github_url: string | null;
  categories: string[];
  models: AIModel[];
  endpoints: ServiceEndpoint[];
  deployments: Deployment[];
  pricing: EndpointPricing[];
  nsfw: boolean;
  is_public: boolean;
  is_validated: boolean;
  is_official: boolean;
  has_billing: boolean;
  sort_idx_featured: number | null;
  n_usages: number;
  created_by: string | null;
  created_at: string | null;
  updated_at: string | null;

  constructor(data: Record<string, any> = {}) {
    this.id = data.id ?? null;
    this.name = data.name ?? null;
    this.display_name = data.display_name ?? null;
    this.description = data.description ?? null;
    this.description_agent = data.description_agent ?? null;
    this.description_agent_updated_at = data.description_agent_updated_at ?? null;
    this.description_user_updated_at = data.description_user_updated_at ?? null;
    this.short_desc = data.short_desc ?? null;
    this.display_img_url = data.display_img_url ?? null;
    this.github_url = data.github_url ?? null;
    this.origin_github_url = data.origin_github_url ?? null;

    this.categories = arr(data.categories);
    this.models = arr(data.models).map((m) => new AIModel(m));
    this.endpoints = arr(data.endpoints).map((e) => new ServiceEndpoint(e));
    this.deployments = arr(data.deployments).map((d) => new Deployment(d));
    this.pricing = arr(data.pricing).map((p) => new EndpointPricing(p));

    this.nsfw = data.nsfw === true;
    this.is_public = data.is_public === true;
    this.is_validated = data.is_validated === true;
    this.is_official = data.is_official === true;
    this.has_billing = data.has_billing === true;

    this.sort_idx_featured = data.sort_idx_featured ?? null;
    this.n_usages = num(data.n_usages) || 0;

    this.created_by = data.created_by ?? null;
    this.created_at = data.created_at ?? null;
    this.updated_at = data.updated_at ?? null;
  }
}
